import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Select, Switch, Space, Typography, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined } from "@ant-design/icons";
import { usePrevious } from "hooks";
import { ImageUpload } from "components/common";
import { Formik } from "formik";
import {
  createMessageTemplateRequest,
  updateMessageTemplateRequest,
} from "providers/MessageTemplateProvider/slice";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

// Component that renders the ImageUpload within Formik context
const FormikImageRenderer = ({ values, setFieldValue, onChange, formattedValue }) => {
  const prevValueRef = React.useRef(formattedValue);
  
  React.useEffect(() => {
    // Only trigger onChange for user uploads, not prop changes
    if (values.image !== prevValueRef.current && values.image !== formattedValue) {
      onChange?.(values.image);
      prevValueRef.current = values.image;
    }
  }, [values.image, formattedValue, onChange]);
  
  return (
    <ImageUpload
      name="image"
      disableCrop={true}
      setFieldValue={setFieldValue}
    />
  );
};

// Wrapper component to make ImageUpload work with Ant Design Form
const AntdImageUploadWrapper = ({ value, onChange }) => {
  // Convert value to proper format for ImageUpload
  const formatImageValue = (val) => {
    if (!val) return null;
    if (typeof val === 'string') {
      return { url: val, objectId: val };
    }
    if (val.url) {
      return { url: val.url, objectId: val.objectId || val.url };
    }
    return val;
  };

  const formattedValue = formatImageValue(value);

  return (
    <div style={{ position: 'relative' }}>
      <Formik
        initialValues={{ image: formattedValue }}
        enableReinitialize={true}
        onSubmit={() => {}}
      >
        {({ values, setFieldValue }) => (
          <FormikImageRenderer 
            values={values}
            setFieldValue={setFieldValue}
            onChange={onChange}
            formattedValue={formattedValue}
          />
        )}
      </Formik>
    </div>
  );
};

const MessageTemplateModal = ({ visible, onCancel, onSuccess, editingTemplate }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [previewContent, setPreviewContent] = useState("");
  
  // Use Form.useWatch to monitor image field without causing re-renders
  const watchedImage = Form.useWatch('image', form);

  const creating = useSelector((state) => state.messageTemplate.creating);
  const updating = useSelector((state) => state.messageTemplate.updating);
  const error = useSelector((state) => state.messageTemplate.error);

  const isLoading = creating || updating;

  useEffect(() => {
    if (visible) {
      console.log('editingTemplate', editingTemplate);
      console.log('editingTemplate.image', editingTemplate?.image);
      console.log('editingTemplate keys', editingTemplate ? Object.keys(editingTemplate) : 'No template');
      
      // Check for different possible image property names
      const possibleImageProps = ['image', 'imageUrl', 'image_url', 'attachment', 'media', 'thumbnail'];
      let imageData = null;
      
      for (const prop of possibleImageProps) {
        if (editingTemplate?.[prop]) {
          console.log(`Found image data in property: ${prop}`, editingTemplate[prop]);
          imageData = editingTemplate[prop];
          break;
        }
      }
      
      if (editingTemplate) {
        form.setFieldsValue({
          title: editingTemplate.title,
          content: editingTemplate.content,
          category: editingTemplate.category,
          isActive: editingTemplate.isActive,
          image: imageData,
        });
        setTags(editingTemplate.tags || []);
        setPreviewContent(editingTemplate.content || "");
        console.log('Form values set:', {
          title: editingTemplate.title,
          content: editingTemplate.content,
          category: editingTemplate.category,
          isActive: editingTemplate.isActive,
          image: imageData,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          title: "",
          content: "",
          category: "greeting",
          isActive: true,
          image: undefined,
        });
        setTags([]);
        setPreviewContent("");
      }
    }
  }, [visible, editingTemplate, form]);

  // Watch for successful completion and close modal
  const prevCreating = usePrevious(creating);
  const prevUpdating = usePrevious(updating);

  useEffect(() => {
    // If was creating/updating and now not (and no error), operation was successful
    if ((prevCreating && !creating && !error) || (prevUpdating && !updating && !error)) {
      console.log('Operation completed successfully, closing modal...');
      form.resetFields();
      setTags([]);
      setPreviewContent("");
      onSuccess();
    }
  }, [creating, updating, error, prevCreating, prevUpdating, form, onSuccess]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (removedTag) => {
    setTags(tags.filter((tag) => tag !== removedTag));
  };

  const handleContentChange = (e) => {
    const content = e.target.value;
    setPreviewContent(renderPreviewContent(content));
  };

  const renderPreviewContent = (content) => {
    if (!content) return "";
    
    // Replace placeholders with example values for preview
    const placeholders = {
      "{customer_name}": "Nguyễn Văn A",
      "{product_name}": "iPhone 15 Pro",
      "{order_id}": "DH123456",
      "{price}": "25.000.000 VNĐ",
      "{date}": new Date().toLocaleDateString("vi-VN"),
      "{phone}": "0123456789",
      "{address}": "123 Nguyễn Văn Cừ, Q.5, TP.HCM",
      "{time}": new Date().toLocaleTimeString("vi-VN"),
    };

    let preview = content;
    Object.entries(placeholders).forEach(([placeholder, value]) => {
      preview = preview.replace(new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"), value);
    });

    return preview;
  };


  const handleSubmit = (values) => {
    console.log('Form submitted with values:', values);
    
    // Extract only the URL from the image data
    let imageUrl = null;
    if (values.image) {
      imageUrl = values.image.url || values.image;
    }
    
    console.log('Extracted image URL:', imageUrl);
    
    const templateData = {
      ...values,
      tags,
    };

    // Only add image field if there's actually an image
    if (imageUrl) {
      templateData.image = imageUrl;
    }

    // Remove any id fields for create operation
    if (!editingTemplate) {
      delete templateData.id;
      delete templateData._id;
    }

    console.log('Template data being sent:', templateData);

    if (editingTemplate) {
      console.log('Dispatching update...');
      dispatch(updateMessageTemplateRequest({ 
        id: editingTemplate._id, 
        ...templateData 
      }));
    } else {
      console.log('Dispatching create...');
      dispatch(createMessageTemplateRequest(templateData));
    }
    
    // Modal will close automatically via useEffect when operation completes successfully
  };

  return (
    <Modal
      title={editingTemplate ? "Chỉnh sửa Template" : "Tạo Template mới"}
      visible={visible}
      onCancel={onCancel}
      width={800}
      footer={null}
      className="message-template-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          isActive: true,
          category: "greeting",
        }}
      >
        <Form.Item
          name="title"
          label="Tiêu đề Template"
          rules={[{ required: true, message: "Vui lòng nhập tiêu đề" }]}
        >
          <Input placeholder="Nhập tiêu đề template" />
        </Form.Item>

        <Form.Item
          name="category"
          label="Danh mục"
          rules={[{ required: true, message: "Vui lòng chọn danh mục" }]}
        >
          <Select placeholder="Chọn danh mục">
            <Option value="greeting">Chào hỏi</Option>
            <Option value="order">Đơn hàng</Option>
            <Option value="support">Hỗ trợ</Option>
            <Option value="promotion">Khuyến mãi</Option>
            <Option value="notification">Thông báo</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Tags">
          <Input
            placeholder="Nhập tag"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onPressEnter={handleAddTag}
            style={{ marginBottom: 8 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTag}>
            Thêm
          </Button>
          <div style={{ marginTop: 8 }}>
            {tags.map((tag) => (
              <span
                key={tag}
                style={{ 
                  display: 'inline-block',
                  background: '#f0f0f0',
                  padding: '4px 8px',
                  margin: '2px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  style={{
                    border: 'none',
                    background: 'none',
                    marginLeft: '8px',
                    cursor: 'pointer',
                    color: '#999'
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </Form.Item>

        <Form.Item
          name="content"
          label="Nội dung Template"
          rules={[{ required: true, message: "Vui lòng nhập nội dung" }]}
        >
          <TextArea
            rows={6}
            placeholder="Nhập nội dung template"
            onChange={handleContentChange}
          />
        </Form.Item>

        <Form.Item
          name="image"
          label="Hình ảnh Template (Tùy chọn)"
          getValueFromEvent={(e) => e}
        >
          <AntdImageUploadWrapper />
        </Form.Item>

        {(previewContent || watchedImage) && (
          <div style={{ marginTop: 16 }}>
            <Text strong>Xem trước:</Text>
            <div className="template-preview preview-active">
              {watchedImage && (
                <div style={{ marginBottom: 12 }}>
                  <img 
                    src={typeof watchedImage === 'string' 
                      ? watchedImage 
                      : watchedImage?.url || watchedImage?.imageUrl || watchedImage?.image_url} 
                    alt="Template"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 200, 
                      borderRadius: 4,
                      objectFit: 'cover'
                    }}
                  />
                </div>
              )}
              {previewContent}
            </div>
          </div>
        )}

        <Divider />

        <Form.Item name="isActive" label="Trạng thái" valuePropName="checked">
          <Switch checkedChildren="Hoạt động" unCheckedChildren="Tắt" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button onClick={onCancel}>Hủy</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {editingTemplate ? "Cập nhật" : "Tạo mới"}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MessageTemplateModal;